import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Video } from '@/mocks/videos';
import { typeLabels } from '@/mocks/videos';

interface HeroCarouselProps {
  slides: Video[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const goTo = (index: number) =>
    setActive((index + slides.length) % slides.length);

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-foreground-950 md:h-[600px]"
      aria-label="推荐视频轮播"
    >
      {slides.map((slide, index) => {
        const badge =
          slide.type === 'apple' && slide.appleCategory
            ? slide.appleCategory
            : typeLabels[slide.type];
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={slide.cover}
              alt={`${slide.title} 焦点图`}
              title={`${slide.title} - 苹果视频`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/10" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-foreground-950/80 to-transparent" />

            <div className="relative flex h-full items-center">
              <div className="w-full px-4 md:px-8 lg:px-16">
                <div className="max-w-2xl mv-animate-up">
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                      {badge}
                    </span>
                    <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-100 backdrop-blur-sm">
                      {slide.year} · {slide.region}
                    </span>
                    <span className="flex items-center gap-1 rounded-md bg-accent-500/90 px-2.5 py-1 text-xs font-medium text-background-50">
                      <i className="ri-star-fill text-xs" aria-hidden="true" />
                      {slide.rating.toFixed(1)}
                    </span>
                  </div>
                  <h2 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                    {slide.title}
                  </h2>
                  <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                    {slide.shortDesc}
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${slide.id}`}
                      className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-play-fill text-lg" aria-hidden="true" />
                      立即观看
                    </Link>
                    <Link
                      to="/search?q=推荐"
                      className="inline-flex items-center gap-2 rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/20"
                    >
                      <i className="ri-compass-3-line text-lg" aria-hidden="true" />
                      发现更多
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => goTo(active - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 -translate-y-1/2 hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/70 md:left-6 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => goTo(active + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 -translate-y-1/2 hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/70 md:right-6 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            aria-label={`切换到第 ${index + 1} 张`}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
}